import { default as axiosBase, AxiosRequestConfig, AxiosResponse } from 'axios'
import Cookies from 'js-cookie'
import { toast } from 'sonner'
import { getCookieOptions } from './cookie-utils'
import { navigate } from './router'
import config from '@/config'

// Global timezone, locale and currency storage
let currentUserTimezone: string | null = null
let currentUserLocale: string | null = null
let currentCurrencyCode: string | null = null

export const setCurrentUserTimezone = (timezone: string | null) => {
  currentUserTimezone = timezone
}

export const setCurrentUserLocale = (locale: string | null) => {
  currentUserLocale = locale
}

export const setCurrentCurrencyCode = (currency: string | null) => {
  currentCurrencyCode = currency
}

const axios = axiosBase.create({
  baseURL: '/api/',
  timeout: config.UPLOAD_TIMEOUT,
  headers: {
    'Content-Type': 'application/json'
  }
})

axios.interceptors.request.use(config => {
  // Don't add auth header to the refresh endpoint — it authenticates via the refresh token in the body
  const isRefreshEndpoint = config.url?.includes('/auth/refresh')
  if (!isRefreshEndpoint) {
    const accessToken = Cookies.get('accessToken')
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
  }

  // Add timezone header if available
  if (currentUserTimezone) {
    config.headers['X-Timezone'] = currentUserTimezone
  }

  // Add currency header — only if not already set by a per-request override
  if (currentCurrencyCode && !config.headers['X-Currency']) {
    config.headers['X-Currency'] = currentCurrencyCode
  }

  // Add an Accept-Language header — only if not already set by a per-request override
  if (currentUserLocale && !config.headers['Accept-Language']) {
    config.headers['Accept-Language'] = currentUserLocale
  }

  return config
})

let isRefreshing = false

type FailedRequest = {
  resolve: (accessToken: string) => void
  reject: (error: unknown) => void
}

let failedQueue: FailedRequest[] = []

const processQueue = (error: unknown, accessToken: string | null) => {
  failedQueue.forEach(prom => {
    if (accessToken) {
      prom.resolve(accessToken)
    } else {
      prom.reject(error)
    }
  })
  failedQueue = []
}

const isJwtExpired = (token: string): boolean => {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) {
      return true
    }
    const payload = JSON.parse(atob(parts[1]))
    return typeof payload.exp !== 'number' || payload.exp * 1000 <= Date.now()
  } catch {
    return true
  }
}

/**
 * Ensures a fresh access token is available.
 * If the current token is expired, attempts to refresh it.
 * Coordinates with the axios interceptor's refresh queue to avoid duplicate refreshes.
 * Returns the valid access token or null if refresh failed.
 */
export const ensureFreshToken = async (): Promise<string | null> => {
  const accessToken = Cookies.get('accessToken')

  if (accessToken && !isJwtExpired(accessToken)) {
    return accessToken
  }

  const refreshToken = Cookies.get('refreshToken')
  if (!refreshToken) {
    return null
  }

  if (isRefreshing) {
    // An axios interceptor refresh is already in progress — wait for it
    return new Promise<string | null>(resolve => {
      failedQueue.push({
        resolve: (token: string) => resolve(token),
        reject: () => resolve(null)
      })
    })
  }

  isRefreshing = true
  try {
    const { data } = await axios.post<{ accessToken: string; refreshToken: string }>('/auth/refresh', {
      refreshToken
    })
    const rememberMe = Cookies.get('rememberMe') === 'true'
    const cookieOptions = getCookieOptions(rememberMe)
    Cookies.set('accessToken', data.accessToken, cookieOptions)
    Cookies.set('refreshToken', data.refreshToken, cookieOptions)
    processQueue(null, data.accessToken)
    return data.accessToken
  } catch (err) {
    processQueue(err, null)
    return null
  } finally {
    isRefreshing = false
  }
}

let isLoggingOut = false

const forceLogout = async (message?: string) => {
  if (isLoggingOut) {
    return
  }
  isLoggingOut = true

  const cookieNames = ['accessToken', 'refreshToken', 'rememberMe']
  cookieNames.forEach(name => {
    Cookies.remove(name)
    Cookies.remove(name, { path: '/' })
    if (typeof document !== 'undefined') {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
    }
  })

  if (message) {
    toast.error(message)
  }

  try {
    const meModule = await import('@/store/me')
    meModule.default.getState().reset()
  } catch {
    // ignore
  } finally {
    navigate('/login')
    isLoggingOut = false
  }
}

axios.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: any) => {
    const originalRequest: AxiosRequestConfig & { _retry?: boolean } = error.config

    // Handle 400+ errors (except 404 which is handled separately) - show toast if a message exists
    // Suppress 401 toast when a refreshToken exists — the interceptor below will attempt to refresh
    const hasRefreshToken = !!Cookies.get('refreshToken')
    const is401 = error.response?.status === 401
    const isRefreshEndpoint = originalRequest.url?.includes('/auth/refresh')
    const isLoginEndpoint = originalRequest.url?.includes('/auth/login')
    // Suppress 401 toasts: either refresh will be attempted, or the refresh endpoint handler above will show it
    // Suppress 423 toasts on the login endpoint: the login form handles the error message itself
    if (
      error.response?.status >= 400 &&
      error.response?.status !== 404 &&
      !(is401 && (hasRefreshToken || isRefreshEndpoint)) &&
      !(error.response?.status === 423 && isLoginEndpoint)
    ) {
      const message = error.response?.data?.message || error.response?.data?.error?.message

      if (message && typeof window !== 'undefined') {
        toast.error(message)
      }
    }

    // Handle 403 Forbidden errors - show full-page access denied
    // Skip for the login endpoint: approval/blocked errors are handled in the login form itself
    if (error.response?.status === 403 && !isLoginEndpoint) {
      // Trigger full-page 403 error display
      if (typeof window !== 'undefined') {
        // Dispatch custom event for global error handling
        window.dispatchEvent(
          new CustomEvent('api-forbidden-error', {
            detail: { error, url: originalRequest.url }
          })
        )
      }
      return Promise.reject(error)
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      const refreshToken = Cookies.get('refreshToken')
      if (!refreshToken) {
        return Promise.reject(error)
      }

      // Refresh endpoint itself returned 401 — token is invalid, logout immediately
      if (originalRequest.url?.includes('/auth/refresh')) {
        processQueue(error, null)
        const message = error?.response?.data?.message || error?.response?.data?.error?.message
        await forceLogout(message)
        return Promise.reject(error)
      }

      if (isRefreshing) {
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(accessToken => {
            originalRequest.headers = {
              ...originalRequest.headers,
              Authorization: `Bearer ${accessToken}`
            }
            return axios(originalRequest)
          })
          .catch(err => Promise.reject(err))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const { data } = await axios.post<{ accessToken: string; refreshToken: string }>('/auth/refresh', {
          refreshToken
        })
        const newToken = data.accessToken
        const newRefreshToken = data.refreshToken

        const rememberMe = Cookies.get('rememberMe') === 'true'
        const cookieOptions = getCookieOptions(rememberMe)

        Cookies.set('accessToken', newToken, cookieOptions)
        Cookies.set('refreshToken', newRefreshToken, cookieOptions)

        axios.defaults.headers.Authorization = `Bearer ${newToken}`
        originalRequest.headers = { ...originalRequest.headers, Authorization: `Bearer ${newToken}` }
        processQueue(null, newToken)

        return axios(originalRequest)
      } catch (refreshError: any) {
        processQueue(refreshError, null)
        // Only force logout when the refresh token is genuinely rejected (401).
        // 5xx errors and network failures are transient — keep the session alive
        // so the user can retry once the backend recovers.
        const refreshStatus = refreshError?.response?.status
        if (refreshStatus === 401) {
          const message = refreshError?.response?.data?.message || refreshError?.response?.data?.error?.message
          await forceLogout(message)
        }
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

export default axios
